import {
  currentCurrency,
  DEFAULT_COUNTRY_CODE,
  DEFAULT_META_TITLE,
  percentageSymbol,
} from '@/constants';
import {
  paymentMethodTypes,
  shippingMethodTypes,
  spinCouponTypes,
} from '@/constants/types';
import { formatPrice } from './number';

export const buildQueryString = (filters) => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });

  return params.toString() ? `?${params.toString()}` : '';
};

export const getCurrentUrl = () => {
  if (typeof window !== 'undefined') {
    // Get current URL and decode it
    const currentUrl = decodeURIComponent(window.location.href);
    return currentUrl;
  }
  return '';
};

export const getProductNameWithModel = (productData, lang) => {
  if (!productData) return '';

  const name = productData?.name?.[lang] || '';
  const model = productData?.model || '';
  return model ? `${name} | ${model}` : name;
};

export const getProductStockStatusString = (quantity, buffer) => {
  if (quantity > 0 && quantity < buffer) {
    return 'Limited Stock!';
  }

  if (quantity <= 0) {
    return 'Out of Stock!';
  }
};

export const getDynamicMetaTitle = (title) => {
  if (typeof title !== 'string' || title === undefined || !title) {
    return DEFAULT_META_TITLE;
  }

  return `${title} | ${DEFAULT_META_TITLE}`;
};

export const getFullAddress = (addressObj) => {
  if (!addressObj) {
    return '';
  }

  // Address - Floor no - District - Upazilla - Area - Post code
  const parts = [
    addressObj.address,
    addressObj.floor,
    addressObj.district?.name?.en,
    addressObj.upazilla?.name?.en,
    addressObj.area?.name?.en,
    addressObj.postal_code,
  ];

  // Filter out empty/undefined values and join with ' - '
  return parts.filter(Boolean).join(', ');
};

export const getShippingFullAddress = (addressObj) => {
  if (!addressObj) {
    return '';
  }

  // Address - Floor no - District - Upazilla - Area - Post code
  const parts = [
    addressObj.address,
    addressObj.floor,
    addressObj.district?.en || addressObj.district?.name?.en,
    addressObj.upazilla?.en || addressObj.upazilla?.name?.en,
    addressObj.area?.en || addressObj.area?.name?.en,
    addressObj.postal_code,
  ];

  // Filter out empty/undefined values and join with ' - '
  return parts.filter(Boolean).join(', ');
};

export const getPhoneWithoutCode = (phone) => phone.replace(/^\+880/, '');

export const getPhoneWithCode = (phone) => `${DEFAULT_COUNTRY_CODE}${phone}`;

export const getImageUrl = (src) => {
  const baseUrl = process.env.NEXT_PUBLIC_ASSET_URL;

  return `${baseUrl}/${src}`;
};

export const getPdfUrl = (src) => {
  const baseUrl = process.env.NEXT_PUBLIC_ASSET_URL;

  return `${baseUrl}/${src}`;
};

export const prepareProductSlugs = (item) => {
  const mainSlug = item?.slug;
  const parentSlug = item?.parent_slug;
  const categorySlug = item?.category?.slug;

  return {
    productSlug: parentSlug || mainSlug,
    variantSlug: parentSlug ? mainSlug : null,
    categorySlug,
  };
};

export const getProductIdsString = async (productArr) => {
  try {
    const ids = productArr.map((product) => product.id).join(',');
    return ids ? `ids=${ids}` : null;
  } catch (error) {
    console.error('Error getting product IDs:', error);
    return null;
  }
};

export const getShippingMethodLabel = (type) => {
  if (type === shippingMethodTypes.storePickup) {
    return 'In Store Pickup';
  }

  return 'Home Delivery';
};

export const getProductTitleNameWithModelAndElement = (product) => {
  if (!product) return '';

  let fullName = '';

  if (product?.model) {
    fullName = [product?.name?.en, product?.model].filter(Boolean).join(' | ');
  } else {
    fullName = product?.name?.en;
  }

  if (
    product?.elements &&
    Array.isArray(product.elements) &&
    product?.elements?.length > 0
  ) {
    const attributeValues = product.elements
      .map((attr) => attr.attribute_value?.en)
      .filter(Boolean);

    let allElementValues = '';

    if (attributeValues?.length > 0) {
      allElementValues = `${attributeValues.join(' | ')}`;
    }

    if (allElementValues?.length > 0) {
      fullName = [fullName, allElementValues].filter(Boolean).join(' | ');
    }
  }

  return fullName;
};

export const getProductTitleNameWithElement = (product) => {
  if (!product) return '';

  let fullName = product?.name?.en;

  if (
    product?.elements &&
    Array.isArray(product.elements) &&
    product.elements.length > 0
  ) {
    const attributeValues = product.elements
      .map((attr) => attr.attribute_value?.en)
      .filter(Boolean);

    let allElementValues = '';

    if (attributeValues?.length > 0) {
      allElementValues = `${attributeValues.join(' | ')}`;
    }

    if (allElementValues?.length > 0) {
      fullName = [fullName, allElementValues].filter(Boolean).join(' | ');
    }
  }

  return fullName;
};

export const getTitleFromSlug = (slug) => {
  if (!slug) return '';
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const getPDFInstallationFeeString = (item) => {
  if (item?.offer_installation_fee > 0) {
    return `৳ ${formatPrice(item.offer_installation_fee)}`;
  }
  if (item.installation_fee > item?.offer_installation_fee) {
    return 'Free';
  }
  return 'N/A';
};

export const isStringExist = (value) => {
  if (typeof value === 'string' && value?.length > 0) {
    return true;
  }

  return false;
};

export const getSpinTheWheelWinningMessage = (couponAmount) =>
  `You’ve just unlocked a ${couponAmount?.discount?.discount_type === spinCouponTypes.amount ? currentCurrency : ''} ${formatPrice(couponAmount?.discount?.discount)} ${couponAmount?.discount?.discount_type === spinCouponTypes.percentage ? percentageSymbol : ''} coupon for your next order of ${formatPrice(couponAmount?.discount?.min_spend)} or more. Simply apply it at checkout to enjoy your discount!`;

export const getCommonSEOImage = ({ imageSrc = null }) => {
  if (imageSrc) {
    return [
      {
        url: getImageUrl(imageSrc),
        width: 800,
        height: 400,
      },
    ];
  }

  return [
    {
      url: `${process.env.NEXT_PUBLIC_APP_URL}/images/default-banner-for-SEO.jpg`,
      width: 800,
      height: 400,
    },
  ];
};

export const getHomePageSEOImage = ({ imageSrc = null }) => {
  if (imageSrc) {
    return [
      {
        url: getImageUrl(imageSrc),
        width: 800,
        height: 400,
      },
    ];
  }

  return [
    {
      url: `${process.env.NEXT_PUBLIC_APP_URL}/images/singer-log-seo-image_1200x600.png`,
      width: 800,
      height: 400,
    },
  ];
};

export const getShippingCostValue = (cost = 0) => {
  if (cost === 0) {
    return 'Free';
  }

  if (cost > 0) {
    return `৳ ${formatPrice(cost)}`;
  }

  return `৳ 0`;
};

export const getPaymentMethodTypeLabel = (paymentMethod, paymentDetails) => {
  if (paymentMethod === paymentMethodTypes.cod) {
    return 'Cash on Delivery';
  }

  if (paymentDetails?.network) {
    return paymentDetails?.network;
  }

  return '';
};

export const getPaymentAccountLabel = (placeOrderDetails, paymentDetails) => {
  if (placeOrderDetails?.payment_method === paymentMethodTypes.cod) {
    return '';
  }

  if (paymentDetails?.account_number) {
    return `*${paymentDetails?.account_number?.slice(-4)}`;
  }

  return '';
};
